import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTodos, Todo } from '../../crm-todos/useData';
import { useTheme } from '../../../contexts/ThemeContext';

export interface PendingTodosProps {
  limit?: number;
  embedded?: boolean;
}

const PendingTodos: React.FC<PendingTodosProps> = ({ limit = 5, embedded }) => {
  const navigate = useNavigate();
  const { todos, isLoading } = useTodos();
  const { colors } = useTheme();
  const cardClass = embedded ? 'min-w-0 flex-1 flex flex-col min-h-0' : `${colors.surface} rounded-xl shadow-sm p-5 ${colors.border} border`;
  const listClass = embedded ? 'flex-1 min-h-0 overflow-y-auto space-y-1' : 'space-y-1';

  const todosArray = Array.isArray(todos) ? todos : [];
  const pending = todosArray
    .filter((todo: Todo) => !todo.todoCompleted)
    .sort((a: Todo, b: Todo) => {
      // Sort by created date (newest first)
      const dateA = new Date(a.todoCreatedAt || 0).getTime();
      const dateB = new Date(b.todoCreatedAt || 0).getTime();
      return dateB - dateA;
    })
    .slice(0, limit);

  if (isLoading) {
    return (
      <div className={cardClass}>
        <h2 className={`text-base font-semibold ${colors.text} mb-3`}>Pending Todos</h2>
        <div className="space-y-2 sm:space-y-3">
          {[1, 2, 3].map((item) => (
            <div key={item} className={`flex items-center space-x-3 sm:space-x-4 pb-2 sm:pb-3 ${colors.border} border-b last:border-0`}>
              <div className={`h-8 w-8 sm:h-10 sm:w-10 rounded ${colors.surfaceHover} animate-pulse flex-shrink-0`}></div>
              <div className="flex-1 min-w-0">
                <div className={`h-3 sm:h-4 ${colors.surfaceHover} animate-pulse rounded w-3/4 mb-1 sm:mb-2`}></div>
                <div className={`h-2.5 sm:h-3 ${colors.surfaceHover} animate-pulse rounded w-1/2`}></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className={cardClass}>
      <div className="flex items-center justify-between mb-4 flex-shrink-0">
        <h2 className={`text-base font-semibold ${colors.text}`}>Pending Todos</h2>
        <button
          onClick={() => navigate('/todos')}
          className="text-sm text-teal-600 hover:text-teal-700 dark:text-teal-400 dark:hover:text-teal-300 font-medium whitespace-nowrap transition-colors"
        >
          View All
        </button>
      </div>
      <div className={listClass}>
        {pending.length > 0 ? (
          pending.map((todo: Todo) => {
            const createdDate = todo.todoCreatedAt ? new Date(todo.todoCreatedAt) : null;
            
            return (
              <div
                key={todo.todoId}
                onClick={() => navigate(`/todos/${todo.todoId}`)}
                className={`flex items-start gap-3 p-3 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800/50 cursor-pointer transition-colors border-b border-gray-100 dark:border-gray-700/50 last:border-0`}
              >
                <div className="flex-shrink-0 mt-1">
                  <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-yellow-500" title="Pending"></div>
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`text-xs sm:text-sm font-medium ${colors.text} line-clamp-2 mb-1`}>{todo.todoDescr || 'Untitled Todo'}</p>
                  
                  <div className={`flex flex-wrap items-center gap-2 sm:gap-3 mt-1.5 sm:mt-2 text-[10px] sm:text-xs ${colors.textMuted}`}>
                    {todo.client?.clientName && (
                      <div className="flex items-center gap-1">
                        <svg className="w-3 h-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                        </svg>
                        <span className="truncate">{todo.client.clientName.length > 20 ? todo.client.clientName.substring(0, 20) + '...' : todo.client.clientName}</span>
                      </div>
                    )}
                    
                    {createdDate && (
                      <div className="flex items-center gap-1">
                        <svg className={`w-3 h-3 flex-shrink-0 ${colors.textMuted}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        <span>{createdDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        ) : (
          <div className={`text-center py-8 ${colors.textMuted}`}>
            <svg className={`w-10 h-10 sm:w-12 sm:h-12 mx-auto mb-2 sm:mb-3 ${colors.borderLight}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
            </svg>
            <p className="text-sm sm:text-base">No pending todos</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PendingTodos;

